import React, { useState, Fragment, memo, useCallback, useMemo } from "react";
import "./WeekView.css";
import styles from "../ResourceMonitor.module.scss";
import { Avatar, Tooltip } from "@fluentui/react-components";
import Tabs from "../../../../components/controls/tabs/Tabs";
import AssignedTasksCard from "../AssignedTasksCard";
import AssignedShiftCard from "../AssignedShiftCard";
import TimeOffCard from "../TimeOffCard";

const ResourceDetailView = memo(({ formBody, title, label, userDetails, shift }) => {
  const [selectedNavItem, setSelectedNavItem] = useState("AssignedTasks");
  
  const renderSelectedComponent = useCallback(() => {
    switch (selectedNavItem) {
      case "AssignedTasks":
        return <AssignedTasksCard />;
      case "AssignedShifts":
        return (
          <AssignedShiftCard formBody={formBody} userDetails={userDetails} />
        );
      default:
        return <TimeOffCard />;
    }
  }, [selectedNavItem, formBody, userDetails]);

  // Memoize capacity calculation
  const capacityPercentage = useMemo(() => {
    return ((shift.workingHours / shift.totalHours) * 100 || 0).toFixed(2);
  }, [shift.workingHours, shift.totalHours]);

  // Memoize shift cell class
  const shiftCellClass = useMemo(() => {
    if (shift.workingHours > shift.totalHours) {
      return styles.OverBooked;
    } else if ((shift.workingHours / shift.totalHours) * 100 > 0) {
      return styles.OccupiedHours;
    } else if ((shift.workingHours / shift.totalHours) * 100 === 0) {
      return styles.FullyAvailable;
    }
    return "";
  }, [shift.workingHours, shift.totalHours]);

  // Memoize status bar width
  const statusBarWidth = useMemo(() => {
    return shift.workingHours > shift.totalHours
      ? "100"
      : ((shift.workingHours / shift.totalHours) * 100).toFixed(2);
  }, [shift.workingHours, shift.totalHours]);

  return (
    <Fragment>
      <div className="d-flex align-items-center justify-content-between mb-4 gap-1">
        <div className="d-flex align-items-center">
          <Avatar
            size={48}
            color="colorful"
            name={userDetails.name}
            image={{ src: userDetails.imageUrl }}
          />
          <h5 className="me-6 ms-6">{userDetails.name}</h5>
          <Tooltip
            content={{
              children: `Capacity: ${capacityPercentage}% Utilized`,
              className: styles.Navtooltip,
            }}
            relationship="label"
            withArrow
            positioning="after"
          >
            <div className={`${styles.shiftCellDetail} ${shiftCellClass}`}>
              <div
                className={`${styles.shiftStatusBar}`}
                style={{ width: `${statusBarWidth}%` }}
              >
                <div className={styles.shiftCell_content}>
                  <div className="d-flex flex-column gap-2">
                    <div className="d-flex gap-1 w-full">
                      <p className="text-sm-600">
                        {shift.workingHours} / {shift.totalHours} hrs
                      </p>
                      <div
                        className={`${styles.shiftCell_bottom} d-flex align-items-center gap-2`}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Tooltip>
        </div>
        <div className={styles.cardHeader}>
          <span className="text-sm-400 greyStrokeColor">{title}</span>
          <span className={styles.dateRange}>{label}</span>
        </div>
      </div>
      <Tabs
        selectedTab={selectedNavItem}
        setSelectedTab={setSelectedNavItem}
        type="shiftDetail"
      />
      <div className={styles.ShiftDetailsTabContent}>
        {renderSelectedComponent()}
      </div>
    </Fragment>
  );
});

ResourceDetailView.displayName = 'ResourceDetailView';

export default ResourceDetailView;
